import React, { useEffect } from "react";
import { useNavigate, type NavigateFunction } from "react-router-dom";
import apiFetch from "../utils/apiFetch";

const getToken = (): string =>
  localStorage.getItem("access_token") || localStorage.getItem("token") || "";

interface ValidationDetail {
  loc?: string[];
  msg?: string;
}

interface ErrorResponse {
  detail?: string | ValidationDetail[];
}

const formatError = (err: unknown): string => {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  const e = err as ErrorResponse;
  if (Array.isArray(e.detail)) {
    return e.detail
      .map((d) => {
        if (typeof d === "string") return d;
        const loc = (d.loc || []).filter((l) => l !== "body").join(".");
        return (d.msg || "validation error") + (loc ? " (" + loc + ")" : "");
      })
      .join("; ");
  }
  if (typeof e.detail === "string") return e.detail;
  return JSON.stringify(err);
};

interface PayrollDefaults {
  pay_period_start: string;
  pay_period_end: string;
  pay_date: string;
  country: string;
  currency: string;
}

const computeDefaults = (): PayrollDefaults => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const pay = new Date(end);
  const fmt = (d: Date): string =>
    d.getFullYear() + "-" +
    String(d.getMonth() + 1).padStart(2, "0") + "-" +
    String(d.getDate()).padStart(2, "0");
  return {
    pay_period_start: fmt(start),
    pay_period_end: fmt(end),
    pay_date: fmt(pay),
    country: "CA",
    currency: "CAD",
  };
};

interface PayRun {
  id: string;
  status?: string;
  updated_at?: string;
  created_at?: string;
  pay_period_start?: string;
}

/**
 * Try to find an existing draft pay run. If found, return its id.
 * If none, return null.
 */
async function findExistingDraft(): Promise<string | null> {
  try {
    const res = await apiFetch("/api/v1/payroll/runs", {
      headers: { Authorization: "Bearer " + getToken() },
    });
    if (!res.ok) return null;
    const runs: PayRun[] = await res.json();
    if (!Array.isArray(runs) || runs.length === 0) return null;
    const drafts = runs.filter((r) => r.status === "draft");
    if (drafts.length === 0) return null;
    drafts.sort((a, b) => {
      const ad = a.updated_at || a.created_at || a.pay_period_start || "";
      const bd = b.updated_at || b.created_at || b.pay_period_start || "";
      return bd.localeCompare(ad);
    });
    return drafts[0].id;
  } catch (e) {
    console.warn("findExistingDraft failed:", e);
    return null;
  }
}

export async function startNewPayroll(navigate: NavigateFunction): Promise<void> {
  try {
    // 1. Check for an existing draft first (smart button: reuse instead of duplicate).
    const existingDraftId = await findExistingDraft();
    if (existingDraftId) {
      navigate("/payroll/run/" + existingDraftId, { replace: true });
      return;
    }

    // 2. None found: create a new draft.
    const body = computeDefaults();
    const res = await apiFetch("/api/v1/payroll/pay-runs/draft", {
      method: "POST",
      headers: {
        Authorization: "Bearer " + getToken(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const errJson = await res.json().catch(() => ({}));
      throw new Error(formatError(errJson));
    }
    const run: PayRun = await res.json();
    if (!run.id) throw new Error("Pay run created without an id.");
    navigate("/payroll/run/" + run.id, { replace: true });
  } catch (e) {
    console.error("Pay run launch failed:", e);
    const msg = e instanceof Error ? e.message : String(e);
    alert("Could not start payroll: " + msg);
    navigate("/payroll/overview", { replace: true });
  }
}

export default function PayrollLauncher(): React.ReactElement {
  const navigate = useNavigate();
  useEffect(() => {
    startNewPayroll(navigate);
  }, [navigate]);
  return (
    <div style={{ padding: 60, textAlign: "center", fontFamily: "inherit", color: "#6B7280", fontSize: 13 }}>
      Starting your payroll run...
    </div>
  );
}
